import log from 'lib/logger';
import ApplicationController from './application_controller';

/**
 * Opens the transaction-details modal on a bank report. Clicking a
 * .view-transaction-details button fetches the transaction's details and
 * renders them in the modal; clicking the backdrop or a [data-dismiss]
 * element closes it.
 *
 * The previous inline turbo:load script stacked on every Turbo visit AND
 * added a fresh document-level click listener on each fire, so listeners
 * grew quadratically and one click fired many duplicate fetches. Delegating
 * a single click handler from the controller element (which Turbo replaces
 * on every body swap) leaves exactly one live handler.
 */
export default class extends ApplicationController {
  static targets = ['modal', 'content'];

  connect() {
    this.onClick = (event) => {
      const button = event.target.closest('.view-transaction-details');
      if (button) {
        event.preventDefault();
        this.showTransaction(button);
        return;
      }

      // Close on backdrop click or close button (scoped to our modal)
      if (
        this.modalTarget.contains(event.target) &&
        (event.target === this.modalTarget || event.target.closest('[data-dismiss="modal"]'))
      ) {
        this.hideModal();
      }
    };
    this.element.addEventListener('click', this.onClick);
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClick);
  }

  showTransaction(button) {
    // Read per-record ids from the DOM at click time
    const { bankReportId, transactionId } = button.dataset;
    const url = `/bank-reports/${bankReportId}/transaction-details/${transactionId}`;

    this.showModal();

    // Show loading spinner
    this.contentTarget.innerHTML = `
      <div class="text-center p-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    `;

    fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'text/html',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((html) => {
        this.contentTarget.innerHTML = html;
      })
      .catch((error) => {
        log.error('Error loading transaction details', error);
        this.contentTarget.innerHTML = `
          <div class="alert alert-danger">
            Error loading transaction details. Please try again.
          </div>
        `;
      });
  }

  showModal() {
    this.modalTarget.classList.add('show');
    this.modalTarget.style.display = 'block';
    document.body.classList.add('modal-open');

    // Create backdrop
    if (!document.querySelector('.modal-backdrop')) {
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);
    }
  }

  hideModal() {
    this.modalTarget.classList.remove('show');
    this.modalTarget.style.display = 'none';
    document.body.classList.remove('modal-open');

    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
  }
}
